import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit, faSave, faTimes, faPlus, faTrash,
  faCheckCircle, faExclamationTriangle, faChevronRight, faChevronDown,
  faArrowLeft, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { getProjects, updateProject } from '../services/projectService';
import pb from '../api/pocketbase';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

export const ProjetsPage = () => {
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null); // 🆕 Projet sélectionné
  const [searchTerm, setSearchTerm] = useState(''); // 🆕 Terme de recherche
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    responsableChantier: '',
    entries: []
  });
  const [newEntry, setNewEntry] = useState({
    rubrique: '',
    sousRubrique: '',
    fournisseurPrevu: '',
    description: '',
    estime: ''
  });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [depensesDG, setDepensesDG] = useState([]);
  const [selectedProjectForChart, setSelectedProjectForChart] = useState(null);
  
  // 🆕 NOUVEAU STATE - Gérer l'expansion des lignes pour voir les détails par fournisseur
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Données de rubriques et sous-rubriques
  const rubriques = {
    "Terrain": ["Achat terrain", "Enregistrement", "Publication foncière", "Commission"],
    "Gros Œuvre": ["Clôture", "Excavation & démolition", "Travaux"],
    "Architecture & Ingénierie": ["Architecture", "Bureau de contrôle", "Assurance décennale", "Urbanisme", "Étude", "Géomètre",
      "Plan BA", "Plan lots techniques", "Notice de sécurité", "Publication règlement de copropriété",
      "Frais de dossier autorisation", "Impôt et taxes", "Taxes diverses / notaire", "Impression & copie plan"],
    "Matériaux de Construction": ["Acier & fer", "Ciment", "Gravier & basalte", "Briques", "Sable",
      "Divers matériels & outillage", "Autres"],
    "Aluminium": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Carrelage": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Cuisines": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "CVC (Climatisation, Ventilation, Chauffage)": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Dressing": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Électricité (CFO + CFA)": ["CFO", "CFA", "COSSUEL", "Vidéophone / Domotique", "Fibre optique", "Caméra de surveillance",
      "Fournitures", "Pose", "Consommation électricité", "Frais sur achat", "Quintal"],
    "Étanchéité & Cuvelage": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Faux Plafond (Plâtre / BA13)": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Peinture": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Plomberie": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Portes": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Meubles & Décoration": ["Meubles offerts à la résidence", "Enseigne", "Tableau camouflage compteur", "Mobilier jardin",
      "Mobilier terrasse piscine", "Salle polyvalente", "Accessoires décoratifs", "Salon hall d'accueil",
      "Équipement salle de sport", "Pergola", "Desk concierge", "Lambris décoratif"],
    "Machineries": ["Groupe électrogène", "Aquarium", "Suppresseur", "Piscine", "Ascenseur"],
    "Réseaux": ["Poste transformateur", "ONAS", "Ageroute", "Sonatel", "SEN EAU"],
    "Sécurité Incendie": ["Sécurité incendie", "Frais sur achat"],
    "Logistique": ["Container dédouanement", "Container déchargement", "Taxes municipales"],
    "Divers": ["Ouvrier & nettoyage", "Divers travaux", "Location matériel chantier", "Réparation matériel",
      "Gardiennage", "Divers matériels & outillage", "Consommation eau/électricité", "Enseigne et décoration"]
  };

  // Fonction utilitaire pour normaliser les chaînes
  const normalize = (str) =>
    (str || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // 🆕 Fonction de filtrage des projets
  const filteredProjets = projets.filter(projet => {
    if (!searchTerm) return true;
    const term = normalize(searchTerm);
    return (
      normalize(projet.name || '').includes(term) ||
      normalize(projet.responsableChantier || '').includes(term) ||
      normalize(projet.description || '').includes(term)
    );
  });

const getReelForLigneBudgetaire = (projet, ligneBudgetaire) => {
  return depensesDG
    .filter(dep => {
      let depRubrique = dep.rubrique;
      let depSousRubrique = dep.sousRubrique;
      let depFournisseur = dep.fournisseur || dep.nomFournisseur || dep.supplier || '';
      if ((!depRubrique || !depSousRubrique) && dep.rubriques) {
        let rubs = dep.rubriques;
        if (typeof rubs === "string") {
          try { rubs = JSON.parse(rubs); } catch { rubs = []; }
        }
        if (Array.isArray(rubs) && rubs.length > 0) {
          depRubrique = depRubrique || rubs[0].rubrique || rubs[0].nom;
          depSousRubrique = depSousRubrique || rubs[0].sousRubrique;
        }
      }
      const depProjetNom = dep.projectNom || dep.nomProjet || dep.projetNom || dep.projet || dep.projetId || "";

      // Correction ici : on compare fournisseurPrevu ET fournisseur
      const fournisseurMatch =
        (ligneBudgetaire.fournisseurPrevu && normalize(ligneBudgetaire.fournisseurPrevu) === normalize(depFournisseur)) ||
        (ligneBudgetaire.fournisseur && normalize(ligneBudgetaire.fournisseur) === normalize(depFournisseur));

      const projetMatch = (projet.id === depProjetNom || normalize(projet.name) === normalize(depProjetNom));
      const rubriqueMatch = normalize(ligneBudgetaire.rubrique) === normalize(depRubrique);
      const sousRubriqueMatch = normalize(ligneBudgetaire.sousRubrique) === normalize(depSousRubrique);

      return projetMatch && rubriqueMatch && sousRubriqueMatch && fournisseurMatch;
    })
    .reduce((sum, dep) => sum + Number(dep.montant || dep.reel || 0), 0);
};

  const toggleRowExpansion = (key) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  // 🆕 NOUVELLE FONCTION - Regrouper les entrées par rubrique
  const groupByRubrique = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      if (!grouped[entry.rubrique]) {
        grouped[entry.rubrique] = [];
      }
      grouped[entry.rubrique].push(entry);
    });
    return grouped;
  };

  // 🆕 NOUVELLE FONCTION - Récupérer le détail des dépenses par fournisseur
  const getDepensesDetailForLigneBudgetaire = (projet, entry) => {
    return depensesDG
      .filter(dep => {
        let depRubrique = dep.rubrique;
        let depSousRubrique = dep.sousRubrique;
        if ((!depRubrique || !depSousRubrique) && dep.rubriques) {
          let rubs = dep.rubriques;
          if (typeof rubs === "string") {
            try { rubs = JSON.parse(rubs); } catch { rubs = []; }
          }
          if (Array.isArray(rubs) && rubs.length > 0) {
            depRubrique = depRubrique || rubs[0].rubrique || rubs[0].nom;
            depSousRubrique = depSousRubrique || rubs[0].sousRubrique;
          }
        }

        const depProjetNom = dep.projectNom || dep.nomProjet || dep.projetNom || dep.projet || dep.projetId || "";

        return (
          (projet.id === depProjetNom || normalize(projet.name) === normalize(depProjetNom)) &&
          normalize(entry.rubrique) === normalize(depRubrique) &&
          normalize(entry.sousRubrique) === normalize(depSousRubrique)
        );
      })
      .map(dep => ({
        ...dep,
        fournisseur: dep.fournisseur || dep.nomFournisseur || dep.supplier || 'Fournisseur non spécifié',
        montant: Number(dep.montant || dep.reel || 0),
        date: dep.created || dep.date || dep.dateDepense
      }));
  };

  // 🆕 NOUVELLE FONCTION - Grouper les dépenses par fournisseur
  const groupDepensesByFournisseur = (depenses) => {
    const grouped = {};
    depenses.forEach(dep => {
      const fournisseur = dep.fournisseur;
      if (!grouped[fournisseur]) {
        grouped[fournisseur] = [];
      }
      grouped[fournisseur].push(dep);
    });
    return grouped;
  };

  // 🆕 NOUVELLE FONCTION - Obtenir le statut intelligent
  const getStatutIntelligent = (budget, reel) => {
    if (budget === 0) return { label: 'Non budgété', icon: '⚫', color: 'gray-400', bgColor: 'gray-100' };
    
    const pourcentage = (reel / budget) * 100;
    
    if (reel === 0) return { label: 'Planifié', icon: '⚪', color: 'gray-600', bgColor: 'gray-100' };
    if (pourcentage < 25) return { label: `${pourcentage.toFixed(0)}%`, icon: '🟢', color: 'green-700', bgColor: 'green-100' };
    if (pourcentage < 50) return { label: `${pourcentage.toFixed(0)}%`, icon: '🟢', color: 'green-700', bgColor: 'green-100' };
    if (pourcentage < 80) return { label: `${pourcentage.toFixed(0)}%`, icon: '🔵', color: 'blue-700', bgColor: 'blue-100' };
    if (pourcentage <= 100) return { label: `${pourcentage.toFixed(0)}%`, icon: '🟡', color: 'yellow-700', bgColor: 'yellow-100' };
    if (pourcentage <= 120) return { label: 'Dépassé', icon: '🔴', color: 'red-700', bgColor: 'red-100' };
    return { label: 'Critique', icon: '🚨', color: 'red-800', bgColor: 'red-200' };
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleEditProject = (project) => {
    setEditingProject(project.id);
    setEditFormData({
      name: project.name || '',
      description: project.description || '',
      responsableChantier: project.responsableChantier || '',
      entries: project.entries || []
    });
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditFormData({
      name: '',
      description: '',
      responsableChantier: '',
      entries: []
    });
  };

 const handleSaveProject = async () => {
  try {
    if (!editFormData.name || !editFormData.responsableChantier) {
      showNotification("Veuillez remplir tous les champs obligatoires", "error");
      return;
    }

    const budgetTotal = editFormData.entries.reduce((sum, e) => sum + (Number(e.estime) || 0), 0);

    const updates = {
      nom: editFormData.name,
      description: editFormData.description,
      responsableChantier: editFormData.responsableChantier,
      rubriques: editFormData.entries,
      montantTotal: budgetTotal,
      date: new Date().toISOString()
    };

    const updated = await updateProject(editingProject, updates);

    // Mise à jour locale immédiate
    const updatedProject = {
      ...updated,
      id: editingProject,
      name: updated.nom || editFormData.name,
      description: updated.description || editFormData.description,
      responsableChantier: updated.responsableChantier || editFormData.responsableChantier,
      entries: updated.rubriques || editFormData.entries,
      updatedAt: updated.updatedAt || updated.date || new Date().toISOString(),
      montantTotal: budgetTotal
    };

    // Mettre à jour la liste des projets
    setProjets(prevProjets =>
      prevProjets.map(proj =>
        proj.id === editingProject ? updatedProject : proj
      )
    );

    // Mettre à jour le projet sélectionné si c'est celui en cours de modification
    if (selectedProject && selectedProject.id === editingProject) {
      setSelectedProject(updatedProject);
    }

    showNotification("Projet mis à jour avec succès", "success");
    setEditingProject(null);

    // Réinitialiser le formulaire
    setEditFormData({
      name: '',
      description: '',
      responsableChantier: '',
      entries: []
    });

    // 🆕 Recharger les projets depuis le serveur pour une meilleure synchronisation
    const updatedProjects = await getProjects();
    setProjets(updatedProjects);
  } catch (error) {
    showNotification("Erreur lors de la mise à jour du projet", "error");
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...editFormData.entries];
    if (field === 'estime') {
      newEntries[index][field] = Number(value);
    } else if (field === 'fournisseurPrevu') {
      // Convertir en majuscules obligatoirement
      newEntries[index][field] = value.toUpperCase();
    } else {
      newEntries[index][field] = value;
    }
    setEditFormData({ ...editFormData, entries: newEntries });
  };

  const handleNewEntryChange = (field, value) => {
    let finalValue = value;
    if (field === 'fournisseurPrevu') {
      // Convertir en majuscules obligatoirement
      finalValue = value.toUpperCase();
    }
    setNewEntry({
      ...newEntry,
      [field]: field === 'estime' ? (finalValue ? Number(finalValue) : '') : finalValue,
      ...(field === 'rubrique' ? { sousRubrique: '' } : {})
    });
  };

  const addNewEntry = () => {
    if (!newEntry.rubrique || !newEntry.sousRubrique || !newEntry.estime) {
      showNotification("Veuillez remplir tous les champs obligatoires", "error");
      return;
    }

    const entry = {
      id: Date.now() + Math.random(),
      rubrique: newEntry.rubrique,
      sousRubrique: newEntry.sousRubrique,
      fournisseurPrevu: newEntry.fournisseurPrevu || 'À définir',
      description: newEntry.description || '',
      estime: Number(newEntry.estime),
      statut: 'planifie'
    };

    setEditFormData({
      ...editFormData,
      entries: [...editFormData.entries, entry]
    });

    setNewEntry({
      rubrique: '',
      sousRubrique: '',
      fournisseurPrevu: '',
      description: '',
      estime: ''
    });
  };

  const deleteEntry = (index) => {
    const newEntries = [...editFormData.entries];
    newEntries.splice(index, 1);
    setEditFormData({ ...editFormData, entries: newEntries });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Pas de date';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Fonction pour le graphique (inchangée)
  const getChartDataForProject = (projet) => {
    if (!projet || !projet.entries || projet.entries.length === 0) {
      return { data: [], budgetTotal: 0, depenseTotal: 0, pourcentageUtilise: 0 };
    }

    const budgetTotal = projet.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0);
    const depenseTotal = projet.entries.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0);
    const pourcentageUtilise = budgetTotal > 0 ? (depenseTotal / budgetTotal) * 100 : 0;
    const restant = Math.max(0, budgetTotal - depenseTotal);

    const data = [
      {
        name: 'Utilisé',
        value: depenseTotal,
        percentage: pourcentageUtilise.toFixed(1)
      },
      {
        name: 'Restant',
        value: restant,
        percentage: (100 - pourcentageUtilise).toFixed(1)
      }
    ];

    return { data, budgetTotal, depenseTotal, pourcentageUtilise: pourcentageUtilise.toFixed(1) };
  };

  const BudgetProgressChart = ({ projet }) => {
    const { data, budgetTotal, depenseTotal, pourcentageUtilise } = getChartDataForProject(projet);

    const COLORS = {
      'Utilisé': '#ef4444',
      'Restant': '#22c55e'
    };

    const renderCustomizedLabel = ({ cx, cy }) => (
      <text
        x={cx}
        y={cy}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-lg font-bold"
      >
        {`${pourcentageUtilise}%`}
      </text>
    );

    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
            <p className="font-medium">{data.name}</p>
            <p className="text-blue-600">
              Montant: {data.value.toLocaleString('fr-FR')} 
            </p>
            <p className="text-gray-600">
              Pourcentage: {data.percentage}%
            </p>
          </div>
        );
      }
      return null;
    };

    if (!projet) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            📊 Progression du Budget
          </h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <p className="text-4xl mb-2">📈</p>
              <p>Sélectionnez un projet pour voir sa progression budgétaire</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            📊 Progression du Budget
          </h3>
          <button
            onClick={() => setSelectedProjectForChart(null)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ✕ Fermer
          </button>
        </div>

        <div className="mb-4">
          <h4 className="font-medium text-blue-800">{projet.name}</h4>
          <p className="text-sm text-gray-600">{projet.responsableChantier}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={renderCustomizedLabel}
                  labelLine={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium text-gray-800 mb-3">Résumé Financier</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Budget Total:</span>
                  <span className="font-medium">{budgetTotal.toLocaleString('fr-FR')} </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dépenses Réelles:</span>
                  <span className="font-medium text-red-600">{depenseTotal.toLocaleString('fr-FR')} </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Budget Restant:</span>
                  <span className={`font-medium ${(budgetTotal - depenseTotal) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(budgetTotal - depenseTotal).toLocaleString('fr-FR')} 
                  </span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Pourcentage Utilisé:</span>
                  <span className={`font-bold text-lg ${pourcentageUtilise > 100 ? 'text-red-600' : pourcentageUtilise > 80 ? 'text-orange-600' : 'text-green-600'}`}>
                    {pourcentageUtilise}%
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {data.map((entry, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className="w-4 h-4 rounded mr-3"
                    style={{ backgroundColor: COLORS[entry.name] }}
                  ></div>
                  <span className="text-sm text-gray-700 flex-grow">{entry.name}</span>
                  <span className="text-sm font-medium">{entry.percentage}%</span>
                </div>
              ))}
            </div>
            {pourcentageUtilise > 100 && (
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                <div className="flex items-center text-red-800">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  <span className="text-sm font-medium">Budget dépassé !</span>
                </div>
              </div>
            )}
            {pourcentageUtilise > 80 && pourcentageUtilise <= 100 && (
              <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                <div className="flex items-center text-orange-800">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  <span className="text-sm font-medium">Attention: {pourcentageUtilise}% du budget utilisé</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const data = await getProjects();
        setProjets(
          data.map(proj => ({
            ...proj,
            name: proj.name || proj.nom || '',
            updatedAt: proj.updatedAt || proj.date || '',
            entries: proj.rubriques || proj.entries || []
          }))
        );
      } catch (err) {
        setProjets([]);
      }
      setLoading(false);
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchDepensesDG = async () => {
      try {
        const data = await pb.collection('depensesValideesDG').getFullList({ sort: '-created' });
        setDepensesDG(data);
      } catch (err) {
        setDepensesDG([]);
      }
    };
    fetchDepensesDG();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      pb.collection('depensesValideesDG').getFullList({ sort: '-created' })
        .then(data => setDepensesDG(data));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleRefresh = () => {
      pb.collection('depensesValideesDG').getFullList({ sort: '-created' })
        .then(data => setDepensesDG(data));
    };

    window.addEventListener('storage', (event) => {
      if (event.key === 'refreshDepensesDG') {
        handleRefresh();
      }
    });

    return () => {
      window.removeEventListener('storage', handleRefresh);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="text-lg font-medium text-gray-700">Chargement...</span>
      </div>
    );
  }

  const groupAndSortEntries = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      if (!grouped[entry.rubrique]) {
        grouped[entry.rubrique] = {};
      }
      if (!grouped[entry.rubrique][entry.sousRubrique]) {
        grouped[entry.rubrique][entry.sousRubrique] = [];
      }
      grouped[entry.rubrique][entry.sousRubrique].push(entry);
    });
    // On retourne un tableau trié
    return Object.entries(grouped).sort().map(([rubrique, sousRubriquesObj]) => ({
      rubrique,
      sousRubriques: Object.entries(sousRubriquesObj).sort().map(([sousRubrique, entriesArr]) => ({
        sousRubrique,
        entries: entriesArr
      }))
    }));
  };


  const renderProjectDetails = (projet) => {
    const isEditing = editingProject === projet.id;

    if (isEditing) {
      return (
        <div className="bg-white rounded-2xl shadow p-6 border border-blue-300">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-blue-800">Modification du projet</h2>
            <div className="flex space-x-2">
              <button
                onClick={handleSaveProject}
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded flex items-center"
              >
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                Enregistrer
              </button>
              <button
                onClick={handleCancelEdit}
                className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded flex items-center"
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Annuler
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Nom du projet *</label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Responsable chantier *</label>
                <input
                  type="text"
                  name="responsableChantier"
                  value={editFormData.responsableChantier}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={editFormData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Tableau d'édition des lignes budgétaires */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Lignes budgétaires individuelles</h3>

              <table className="w-full text-sm border-collapse border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Rubrique</th>
                    <th className="p-2 text-left">Sous-rubrique</th>
                    <th className="p-2 text-left">Fournisseur</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-center">Budget ()</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {editFormData.entries.map((entry, index) => (
                    <tr key={index} className="border-t border-gray-300">
                      <td className="p-2">
                        <select
                          value={entry.rubrique}
                          onChange={(e) => handleEntryChange(index, 'rubrique', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                        >
                          <option value="">Sélectionner</option>
                          {Object.keys(rubriques).map(rub => (
                            <option key={rub} value={rub}>{rub}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <select
                          value={entry.sousRubrique}
                          onChange={(e) => handleEntryChange(index, 'sousRubrique', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                          disabled={!entry.rubrique}
                        >
                          <option value="">Sélectionner</option>
                          {entry.rubrique && rubriques[entry.rubrique] && rubriques[entry.rubrique].map(sousRub => (
                            <option key={sousRub} value={sousRub}>{sousRub}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          value={entry.fournisseurPrevu || ''}
                          onChange={(e) => handleEntryChange(index, 'fournisseurPrevu', e.target.value.toUpperCase())}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs uppercase"
                          placeholder="FOURNISSEUR"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="text"
                          value={entry.description || ''}
                          onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                          placeholder="Description"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={entry.estime}
                          onChange={(e) => handleEntryChange(index, 'estime', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-right text-xs"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <button
                          onClick={() => deleteEntry(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Formulaire pour nouvelle entrée */}
                  <tr className="border-t border-gray-300 bg-gray-50">
                    <td className="p-2">
                      <select
                        value={newEntry.rubrique}
                        onChange={(e) => handleNewEntryChange('rubrique', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                      >
                        <option value="">Sélectionner une rubrique</option>
                        {Object.keys(rubriques).map(rub => (
                          <option key={rub} value={rub}>{rub}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <select
                        value={newEntry.sousRubrique}
                        onChange={(e) => handleNewEntryChange('sousRubrique', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                        disabled={!newEntry.rubrique}
                      >
                        <option value="">Sélectionner une sous-rubrique</option>
                        {newEntry.rubrique && rubriques[newEntry.rubrique] && rubriques[newEntry.rubrique].map(sousRub => (
                          <option key={sousRub} value={sousRub}>{sousRub}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        value={newEntry.fournisseurPrevu}
                        onChange={(e) => handleNewEntryChange('fournisseurPrevu', e.target.value.toUpperCase())}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs uppercase"
                        placeholder="FOURNISSEUR PRÉVU"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        value={newEntry.description}
                        onChange={(e) => handleNewEntryChange('description', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs"
                        placeholder="Description"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={newEntry.estime}
                        onChange={(e) => handleNewEntryChange('estime', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-right text-xs"
                        placeholder="Budget"
                      />
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={addNewEntry}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </td>
                  </tr>

                  {/* Total */}
                  <tr className="bg-blue-100 font-semibold">
                    <td colSpan="4" className="p-2 text-right">Total projet</td>
                    <td className="p-2 text-center">
                      {editFormData.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0).toLocaleString('fr-FR')} 
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow p-6 border border-gray-200">
        {/* Bouton retour */}
        <div className="mb-4">
          <button
            onClick={() => setSelectedProject(null)}
            className="flex items-center text-blue-600 hover:text-blue-800 font-semibold"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Retour à la liste des projets
          </button>
        </div>

        {/* ID du projet */}
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3 flex items-center">
          <div className="flex-grow min-w-0">
            <span className="font-bold text-blue-800">ID du Projet: </span>
            <span className="font-mono text-blue-700 select-all break-all text-sm">{projet.id}</span>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(projet.id);
              showNotification('ID copié!', 'success');
            }}
            className="ml-2 bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm flex-shrink-0"
          >
            Copier
          </button>
        </div>

        {/* En-tête du projet */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
          <div className="flex-grow min-w-0">
            <h2 className="text-xl font-bold text-gray-800 break-words">
              {projet.name || 'Nom inconnu'}
            </h2>
            {projet.responsableChantier && (
              <p className="text-base text-blue-700 mt-1">
                <span className="font-semibold">Responsable:</span> {projet.responsableChantier}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Mise à jour : {formatDate(projet.updatedAt)}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setSelectedProjectForChart(projet)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded flex items-center text-sm"
            >
              <span className="mr-1">📊</span>
              Graphique
            </button>
            <button
              onClick={() => handleEditProject(projet)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded flex items-center"
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" />
              Modifier
            </button>
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-800 mb-2">Description du projet</h3>
          <div className="bg-gray-50 rounded-lg p-4 border">
            <p className="text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
              {projet.description || 'Aucune description disponible'}
            </p>
          </div>
        </div>

        {/* Lignes budgétaires */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-4">Lignes budgétaires</h3>
          {Array.isArray(projet.entries) && projet.entries.length > 0 ? (
            <div className="space-y-4">
              {/* Version mobile et tablette - Cards empilées */}
              <div className="block lg:hidden space-y-3">
                {Object.entries(groupByRubrique(projet.entries)).map(([rubrique, sousRubriques], idx) => {
                  const budgetRubrique = sousRubriques.reduce((sum, item) => sum + (item.estime || 0), 0);
                  const reelRubrique = sousRubriques.reduce((sum, item) => sum + getReelForLigneBudgetaire(projet, item), 0);
                  const ecartRubrique = budgetRubrique - reelRubrique;
                  const statutRubrique = getStatutIntelligent(budgetRubrique, reelRubrique);

                  return (
                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* En-tête de rubrique */}
                      <div className="bg-blue-100 px-4 py-3 border-b">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {rubrique.includes('Terrain') ? '🏗️' :
                               rubrique.includes('Matériaux') ? '🧱' :
                               rubrique.includes('Électricité') ? '⚡' :
                               rubrique.includes('Plomberie') ? '🚰' :
                               rubrique.includes('Architecture') ? '📐' :
                               rubrique.includes('Aluminium') ? '🔩' :
                               rubrique.includes('Carrelage') ? '🏺' :
                               rubrique.includes('Peinture') ? '🎨' :
                               rubrique.includes('Sécurité') ? '🔥' :
                               '📦'}
                            </span>
                            <span className="font-bold text-blue-900 text-sm break-words">{rubrique}</span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold bg-${statutRubrique.bgColor} text-${statutRubrique.color} flex items-center gap-1`}>
                            <span>{statutRubrique.icon}</span>
                            <span>{statutRubrique.label}</span>
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                          <div className="text-center">
                            <span className="text-gray-600">Budget</span>
                            <div className="font-bold text-blue-800">{budgetRubrique.toLocaleString('fr-FR')}</div>
                          </div>
                          <div className="text-center">
                            <span className="text-gray-600">Réel</span>
                            <div className="font-bold text-red-600">{reelRubrique.toLocaleString('fr-FR')}</div>
                          </div>
                          <div className="text-center">
                            <span className="text-gray-600">Écart</span>
                            <div className={`font-bold ${ecartRubrique >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {ecartRubrique >= 0 ? '+' : ''}{ecartRubrique.toLocaleString('fr-FR')}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sous-rubriques */}
                      <div className="divide-y divide-gray-100">
                        {sousRubriques.map((entry, i) => {
                          const reelEntry = getReelForLigneBudgetaire(projet, entry);
                          const ecartEntry = (entry.estime || 0) - reelEntry;
                          const depensesDetail = getDepensesDetailForLigneBudgetaire(projet, entry);
                          const groupedByFournisseur = groupDepensesByFournisseur(depensesDetail);
                          const fournisseurs = Object.keys(groupedByFournisseur);
                          const statutEntry = getStatutIntelligent(entry.estime || 0, reelEntry);

                          return (
                            <div key={i} className="p-3 bg-white">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-purple-700 text-sm break-words flex-grow pr-2">
                                  {entry.sousRubrique}
                                </h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold bg-${statutEntry.bgColor} text-${statutEntry.color} flex items-center gap-1 flex-shrink-0`}>
                                  <span>{statutEntry.icon}</span>
                                  <span>{statutEntry.label}</span>
                                </span>
                              </div>
                              
                              {entry.description && (
                                <p className="text-xs text-gray-600 mb-2 break-words italic">
                                  {entry.description}
                                </p>
                              )}

                              <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                                <div className="text-center">
                                  <span className="text-gray-500">Budget</span>
                                  <div className="font-medium">{(entry.estime || 0).toLocaleString('fr-FR')}</div>
                                </div>
                                <div className="text-center">
                                  <span className="text-gray-500">Réel</span>
                                  <div className="font-medium">{reelEntry.toLocaleString('fr-FR')}</div>
                                </div>
                                <div className="text-center">
                                  <span className="text-gray-500">Écart</span>
                                  <div className={`font-medium ${ecartEntry >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {ecartEntry >= 0 ? '+' : ''}{ecartEntry.toLocaleString('fr-FR')}
                                  </div>
                                </div>
                              </div>

                              {fournisseurs.length > 0 && (
                              <div className="bg-purple-50 rounded p-2 mt-2">
                                <span className="text-xs text-purple-600 font-medium">Fournisseur(s) :</span>
                                <div className="text-xs text-purple-800 break-words space-y-1 mt-1">
                                  {fournisseurs.map((fournisseur, fIdx) => {
                                    // Cherche le budget du fournisseur dans l'entrée
                                    let budgetFournisseur = null;
                                    if (entry.fournisseur && entry.fournisseur === fournisseur && entry.budgetfournisseur) {
                                      budgetFournisseur = entry.budgetfournisseur;
                                    } else if (groupedByFournisseur[fournisseur]) {
                                      // Si plusieurs dépenses, somme les montants
                                      budgetFournisseur = groupedByFournisseur[fournisseur].reduce((sum, dep) => sum + (dep.budgetfournisseur || dep.montant || 0), 0);
                                    }
                                    return (
                                      <div key={fIdx} className="flex justify-between items-center">
                                        <span className="break-words flex-grow pr-2">{fournisseur}</span>
                                        <span className="font-medium flex-shrink-0">
                                          {budgetFournisseur ? `${Number(budgetFournisseur).toLocaleString('fr-FR')} FCFA` : '-'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Total projet - Version mobile */}
                <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                  <div className="flex items-center justify-center mb-3">
                    <span className="text-xl mr-2">🏗️</span>
                    <span className="font-bold text-green-800 text-lg">TOTAL PROJET</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <span className="text-xs text-green-700">Budget Total</span>
                      <div className="font-bold text-green-800 text-lg">
                        {projet.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0).toLocaleString('fr-FR')}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-green-700">Réel Total</span>
                      <div className="font-bold text-green-800 text-lg">
                        {projet.entries.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0).toLocaleString('fr-FR')}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-green-700">Écart Total</span>
                      <div className="font-bold text-green-800 text-lg">
                        {(projet.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0) -
                          projet.entries.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0)
                        ).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Version desktop - Tableau complet */}
              <div className="hidden lg:block">
                <div className="rounded-2xl shadow-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100">
                  <table className="w-full table-auto text-sm">
                    <thead className="bg-blue-700 text-white sticky top-0 z-10 shadow">
                      <tr>
                        <th className="px-4 py-3 text-left rounded-tl-2xl">Rubrique / Sous-rubrique</th>
                        <th className="px-4 py-3 text-center w-32">Budget alloué</th>
                        <th className="px-4 py-3 text-center w-32">Réel</th>
                        <th className="px-4 py-3 text-center w-32">Écart</th>
                        <th className="px-4 py-3 text-center bg-blue-600">Fournisseur(s)</th>
                        <th className="px-4 py-3 text-center rounded-tr-2xl">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                    {groupAndSortEntries(projet.entries).map(
                      ({ rubrique, sousRubriques }, idx) => {
                        // Calculs pour la rubrique
                        const allEntries = sousRubriques.flatMap(sr => sr.entries);
                        const budgetRubrique = allEntries.reduce((sum, item) => sum + (item.estime || 0), 0);
                        const reelRubrique = allEntries.reduce((sum, item) => sum + getReelForLigneBudgetaire(projet, item), 0);
                        const ecartRubrique = budgetRubrique - reelRubrique;
                        const statutRubrique = getStatutIntelligent(budgetRubrique, reelRubrique);

                        return (
                          <React.Fragment key={idx}>
                            {/* Ligne principale de la rubrique */}
                            <tr className="bg-gradient-to-r from-blue-100 to-blue-50 font-bold border-b-2 border-blue-200">
                              <td className="px-4 py-3 text-blue-900 rounded-l-xl">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg flex-shrink-0">
                                    {rubrique.includes('Terrain') ? '🏗️' :
                                    rubrique.includes('Matériaux') ? '🧱' :
                                    rubrique.includes('Électricité') ? '⚡' :
                                    rubrique.includes('Plomberie') ? '🚰' :
                                    rubrique.includes('Architecture') ? '📐' :
                                    rubrique.includes('Aluminium') ? '🔩' :
                                    rubrique.includes('Carrelage') ? '🏺' :
                                    rubrique.includes('Peinture') ? '🎨' :
                                    rubrique.includes('Sécurité') ? '🔥' :
                                    '📦'}
                                  </span>
                                  <span className="break-words">{rubrique}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center font-bold">
                                {budgetRubrique.toLocaleString('fr-FR')}
                              </td>
                              <td className="px-4 py-3 text-center font-bold">
                                {reelRubrique.toLocaleString('fr-FR')}
                              </td>
                              <td className={`px-4 py-3 text-center font-bold ${ecartRubrique >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {ecartRubrique >= 0 ? '+' : ''}{ecartRubrique.toLocaleString('fr-FR')}
                              </td>
                              <td className="px-4 py-3 text-center bg-blue-50">
                                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                                  {allEntries.length} ligne{allEntries.length > 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center rounded-r-xl">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold bg-${statutRubrique.bgColor} text-${statutRubrique.color} inline-flex items-center gap-1`}>
                                  <span>{statutRubrique.icon}</span>
                                  <span>{statutRubrique.label}</span>
                                </span>
                              </td>
                            </tr>

                            {/* Lignes des sous-rubriques */}
                            {sousRubriques.map(({ sousRubrique, entries }, srIdx) =>
                              entries.map((entry, i) => {
                                const reelEntry = getReelForLigneBudgetaire(projet, entry);
                                const ecartEntry = (entry.estime || 0) - reelEntry;
                                const depensesDetail = getDepensesDetailForLigneBudgetaire(projet, entry);
                                const groupedByFournisseur = groupDepensesByFournisseur(depensesDetail);
                                const fournisseurs = Object.keys(groupedByFournisseur);
                                const hasMultipleFournisseurs = fournisseurs.length > 1;
                                const rowKey = `${projet.id}-${entry.rubrique}-${entry.sousRubrique}`;
                                const isExpanded = expandedRows.has(rowKey);
                                const statutEntry = getStatutIntelligent(entry.estime || 0, reelEntry);

                                return (
                                  <React.Fragment key={`${srIdx}-${i}`}>
                                    <tr className="border-t border-blue-100 hover:bg-blue-50 transition-colors">
                                      <td className="px-4 py-2 pl-8">
                                        <div className="flex items-center">
                                          <div className="min-w-0 flex-grow">
                                            <div className="font-medium text-purple-700">{sousRubrique}</div>
                                            {entry.description && (
                                              <div className="text-xs text-gray-500 italic mt-1 break-words">{entry.description}</div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-center font-medium">
                                        {(entry.estime || 0).toLocaleString('fr-FR')}
                                      </td>
                                      <td className="px-4 py-2 text-center font-medium">
                                        {reelEntry.toLocaleString('fr-FR')}
                                      </td>
                                      <td className={`px-4 py-2 text-center font-medium ${ecartEntry >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {ecartEntry >= 0 ? '+' : ''}{ecartEntry.toLocaleString('fr-FR')}
                                      </td>
                                      <td className="px-4 py-2 text-center bg-blue-50">
                                        {entry.fournisseur || entry.fournisseurPrevu ? (
                                          <div>
                                            <span className="font-medium text-purple-700">{entry.fournisseur || entry.fournisseurPrevu}</span>
                                            <span className="ml-2 text-xs text-gray-700">
                                              {entry.budgetfournisseur ? `${Number(entry.budgetfournisseur).toLocaleString()} FCFA` : ''}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400 italic">Aucun</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-center">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold bg-${statutEntry.bgColor} text-${statutEntry.color} inline-flex items-center gap-1`}>
                                          <span>{statutEntry.icon}</span>
                                          <span>{statutEntry.label}</span>
                                        </span>
                                      </td>
                                    </tr>
                                    {/* Lignes de détail par fournisseur */}
                                    {isExpanded && hasMultipleFournisseurs && fournisseurs.map((fournisseur, fIdx) => {
                                      const depensesFournisseur = groupedByFournisseur[fournisseur];
                                      const totalFournisseur = depensesFournisseur.reduce((sum, dep) => sum + dep.montant, 0);

                                      return (
                                        <tr key={fIdx} className="bg-gradient-to-r from-blue-50 to-purple-50 text-sm text-gray-600 border-l-4 border-purple-300">
                                          <td></td>
                                          <td></td>
                                          <td className="px-4 py-1 text-center font-semibold text-purple-700">
                                            {totalFournisseur.toLocaleString()}
                                          </td>
                                          <td></td>
                                          <td className="px-4 py-1 text-center bg-blue-50">
                                            <div className="flex flex-col items-center">
                                              <span className="font-medium text-purple-700">{fournisseur}</span>
                                              <span className="text-xs text-gray-500">
                                                ({depensesFournisseur.length} dépense{depensesFournisseur.length > 1 ? 's' : ''})
                                              </span>
                                            </div>
                                          </td>
                                          <td></td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </React.Fragment>
                        );
                      }
                    )}

                    {/* Total global du projet */}
                    <tr className="bg-gradient-to-r from-green-300 to-green-200 font-bold border-t-4 border-green-500">
                      <td className="px-4 py-3 text-green-800 rounded-bl-xl">
                        <div className="flex items-center gap-2">
                          <span className="text-xl flex-shrink-0">🏗️</span>
                          <span className="text-lg">TOTAL PROJET</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-green-800 text-lg">
                        {projet.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-green-800 text-lg">
                        {projet.entries.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-green-800 text-lg">
                        {(projet.entries.reduce((total, entry) => total + (Number(entry.estime) || 0), 0) -
                          projet.entries.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0)
                        ).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-green-800 bg-blue-50">
                        <span className="text-sm">
                          {(() => {
                            const allFournisseurs = new Set();
                            projet.entries.forEach(entry => {
                              const depenses = getDepensesDetailForLigneBudgetaire(projet, entry);
                              depenses.forEach(dep => allFournisseurs.add(dep.fournisseur));
                            });
                            return allFournisseurs.size;
                          })()} fournisseur{(() => {
                            const allFournisseurs = new Set();
                            projet.entries.forEach(entry => {
                              const depenses = getDepensesDetailForLigneBudgetaire(projet, entry);
                              depenses.forEach(dep => allFournisseurs.add(dep.fournisseur));
                            });
                            return allFournisseurs.size > 1 ? 's' : '';
                          })()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-green-800 rounded-br-xl">
                        <span className="text-sm">
                          {projet.entries.length} ligne{projet.entries.length > 1 ? 's' : ''}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <span className="text-gray-500">Aucune ligne budgétaire définie</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        📋 Contrôle budgétaire avancé - Vue unifiée
      </h1>

      {/* Graphique de progression du budget */}
      {selectedProjectForChart && (
        <div className="mb-8">
          <BudgetProgressChart projet={selectedProjectForChart} />
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-20 right-6 p-4 rounded shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-100 text-green-800 border-l-4 border-green-500' :
          'bg-red-100 text-red-800 border-l-4 border-red-500'
        }`}>
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={notification.type === 'success' ? faCheckCircle : faExclamationTriangle}
              className={notification.type === 'success' ? 'text-green-600 mr-2' : 'text-red-600 mr-2'}
            />
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* 🆕 CONTENU PRINCIPAL CONDITIONNEL */}
      {selectedProject ? (
        // Affichage des détails d'un projet sélectionné
        renderProjectDetails(selectedProject)
      ) : (
        // Vue en cartes des projets
        <div className="space-y-6">
          {/* Barre de recherche */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="relative max-w-md">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Rechercher un projet, responsable ou description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {searchTerm && (
              <p className="mt-2 text-sm text-gray-600">
                {filteredProjets.length} résultat{filteredProjets.length > 1 ? 's' : ''} trouvé{filteredProjets.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Grille des cartes de projets */}
          {filteredProjets.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-lg shadow">
              <p className="text-gray-500">
                {searchTerm ? 'Aucun projet ne correspond à votre recherche' : 'Aucun projet disponible'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjets.map((projet) => {
                // Calcul des métriques pour chaque carte
                const budgetTotal = projet.entries?.reduce((total, entry) => total + (Number(entry.estime) || 0), 0) || 0;
                const depenseTotal = projet.entries?.reduce((total, entry) => total + getReelForLigneBudgetaire(projet, entry), 0) || 0;
                const pourcentageUtilise = budgetTotal > 0 ? (depenseTotal / budgetTotal) * 100 : 0;
                
                return (
                  <div
                    key={projet.id}
                    onClick={() => setSelectedProject(projet)}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-gray-200 hover:border-blue-300"
                  >
                    {/* En-tête de la carte */}
                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-800 line-clamp-2">
                          {projet.name || 'Nom inconnu'}
                        </h3>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProjectForChart(projet);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                            title="Voir le graphique"
                          >
                            📊
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProject(projet);
                            }}
                            className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-full transition-colors"
                            title="Modifier le projet"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Responsable chantier */}
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          👷 {projet.responsableChantier || 'Non assigné'}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {projet.description || 'Aucune description disponible'}
                      </p>

                      {/* Date de mise à jour */}
                      <p className="text-xs text-gray-400">
                        Mis à jour : {formatDate(projet.updatedAt)}
                      </p>
                    </div>

                    {/* Métriques financières */}
                    <div className="p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Budget Total</p>
                          <p className="text-sm font-bold text-blue-700">
                            {budgetTotal.toLocaleString()} F
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Dépensé</p>
                          <p className="text-sm font-bold text-red-600">
                            {depenseTotal.toLocaleString()} F
                          </p>
                        </div>
                      </div>

                      {/* Barre de progression */}
                      <div className="mb-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-600">Progression</span>
                          <span className={`text-xs font-bold ${
                            pourcentageUtilise > 100 ? 'text-red-600' : 
                            pourcentageUtilise > 80 ? 'text-orange-600' : 
                            'text-green-600'
                          }`}>
                            {pourcentageUtilise.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              pourcentageUtilise > 100 ? 'bg-red-500' :
                              pourcentageUtilise > 80 ? 'bg-orange-400' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(pourcentageUtilise, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Indicateurs */}
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {projet.entries?.length || 0} ligne{(projet.entries?.length || 0) > 1 ? 's' : ''} budgétaire{(projet.entries?.length || 0) > 1 ? 's' : ''}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          pourcentageUtilise > 100 ? 'bg-red-100 text-red-700' :
                          pourcentageUtilise > 80 ? 'bg-orange-100 text-orange-700' :
                          pourcentageUtilise > 0 ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {pourcentageUtilise > 100 ? '🚨 Dépassé' :
                           pourcentageUtilise > 80 ? '⚠️ Attention' :
                           pourcentageUtilise > 0 ? '🔄 En cours' :
                           '⏸️ Planifié'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
